'use client'

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Logo } from "@/components/ui/Logo";
import { NIGERIA_PREFIX, toNationalDigits } from "@/lib/phone";
import { safeNext } from "@/lib/safe-next";
import { createClient } from "@/utils/supabase/client";

type Step = "phone" | "code";
/** "redirecting" keeps the form disabled across the post-verify navigation. */
type Pending = "send" | "verify" | "resend" | "redirecting" | null;

/** Matches Supabase's per-phone OTP cooldown, so we don't invite a rejected resend. */
const RESEND_COOLDOWN_SECONDS = 60;

const primaryButton =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60";

/** Seconds Supabase wants us to wait, parsed out of its rate-limit message. */
function parseRetryAfter(message: string): number | null {
  const match = /(\d+)\s*second/i.exec(message);
  return match ? Number(match[1]) : null;
}

/**
 * Supabase's raw auth errors are developer-facing; map the ones customers
 * actually hit. Step-aware, because "invalid" means different things on the two
 * screens — an "invalid phone number" on the phone step must not render as
 * "that code isn't right" next to a field that holds no code.
 *
 * The hook's correlation id (`ref: abc12345`) is preserved so a customer report
 * can be traced to a server log line, and the raw text is always logged.
 */
function friendlyError(raw: string, step: Step): string {
  console.error("[login]", raw);
  const message = raw.toLowerCase();
  const ref = /\(ref: ([0-9a-f]{8})\)/.exec(raw)?.[1];
  const withRef = (text: string) => (ref ? `${text} (ref ${ref})` : text);

  if (message.includes("security purposes") || message.includes("rate limit") || message.includes("too many")) {
    return "Too many requests. Please wait a moment before trying again.";
  }
  if (message.includes("nigerian mobile") || message.includes("unsupported destination")) {
    return "We can only send codes to Nigerian mobile numbers right now.";
  }
  if (step === "code" && message.includes("expired")) {
    return "That code has expired. Request a new one.";
  }
  if (step === "code" && (message.includes("invalid") || message.includes("incorrect"))) {
    return "That code isn't right. Check the message and try again.";
  }
  if (step === "phone" && message.includes("invalid")) {
    return "That phone number doesn't look right. Check it and try again.";
  }
  if (message.includes("sms") || message.includes("hook") || message.includes("provider") || message.includes("delivery")) {
    return withRef("We couldn't send your code right now. Please try again in a moment.");
  }
  return withRef("Something went wrong. Please try again.");
}

function getErrorMessage(error: unknown, step: Step): string {
  return friendlyError(error instanceof Error ? error.message : "", step);
}

/**
 * The login form. `next` arrives ALREADY VALIDATED by app/login/page.tsx, which
 * runs safeNext() on the server; it is re-validated here immediately before
 * navigating because that call is what stops a `javascript:` URL executing in
 * the context of this page, and it must not be refactorable away from the one
 * line that consumes it.
 */
export function LoginForm({ next }: { next: string }) {
  const [supabase] = useState(() => createClient());
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const didMount = useRef(false);

  const [step, setStep] = useState<Step>("phone");
  const [phoneInput, setPhoneInput] = useState("");
  const [phone, setPhone] = useState(""); // normalized E.164, set once a code is sent
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fieldInvalid, setFieldInvalid] = useState(false);
  // Cooldown is stored as a DEADLINE, not a tick count: background tabs (and iOS
  // Safari suspension) freeze timers, which is exactly when the user is away
  // reading the SMS. It's keyed to the number it applies to, because Supabase's
  // limit is per phone — switching to a different number should not be blocked.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownPhone, setCooldownPhone] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const busy = pending !== null;

  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = () => setCooldown(Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  function startCooldown(phoneE164: string, seconds = RESEND_COOLDOWN_SECONDS) {
    setCooldownPhone(phoneE164);
    setCooldownUntil(Date.now() + seconds * 1000);
  }

  // Manage focus across step changes — but skip the initial mount so we don't
  // grab focus on first paint: code field when advancing, phone field on return.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (step === "code") codeInputRef.current?.focus();
    else phoneInputRef.current?.focus();
  }, [step]);

  function resetMessages() {
    setError(null);
    setNotice(null);
    setFieldInvalid(false);
  }

  async function handleSendCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    resetMessages();

    const national = toNationalDigits(phoneInput);
    if (national.length !== 10) {
      setError("Enter a valid Nigerian phone number, e.g. 0801 234 5678.");
      setFieldInvalid(true);
      return;
    }

    const e164 = `${NIGERIA_PREFIX}${national}`;
    // Supabase rate-limits per phone number, so only block a repeat of the SAME one.
    if (cooldown > 0 && e164 === cooldownPhone) {
      setError(`Please wait ${cooldown}s before requesting another code.`);
      return;
    }

    setPending("send");
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({ phone: e164 });
      if (otpError) {
        setError(friendlyError(otpError.message, "phone"));
        // A failed send has usually already consumed a rate-limit token, so hold
        // the cooldown rather than clearing it — adopt Supabase's own wait if given.
        startCooldown(e164, parseRetryAfter(otpError.message) ?? RESEND_COOLDOWN_SECONDS);
        return;
      }
      setPhone(e164);
      setCode("");
      setStep("code");
      startCooldown(e164);
    } catch (err) {
      setError(getErrorMessage(err, "phone"));
    } finally {
      setPending(null);
    }
  }

  async function handleVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    resetMessages();

    if (code.length !== 6) {
      setError("Enter the 6-digit code from the text message.");
      setFieldInvalid(true);
      return;
    }

    setPending("verify");
    let redirecting = false;
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        phone,
        token: code,
        type: "sms",
      });
      if (verifyError) {
        setError(friendlyError(verifyError.message, "code"));
        return;
      }
      // A HARD navigation, not router.replace(). verifyOtp() has just written the
      // auth cookie in the browser; a client-side navigation into an auth-gated
      // page (/account/bookings, or a book route) can beat that write, getUser()
      // returns null, and the customer is bounced straight back to the form they
      // just completed. A full document load always carries the cookie.
      //
      // assign(), not replace(): this leaves the login page in history, but the
      // page it lands on is signed-in and Back re-runs the server component, which
      // no longer has a reason to show the form. The race is the bigger risk.
      redirecting = true;
      setPending("redirecting");
      window.location.assign(safeNext(next));
    } catch (err) {
      setError(getErrorMessage(err, "code"));
    } finally {
      // Stay disabled through the navigation: re-enabling here would let a double
      // tap re-submit a now-consumed token and flash an error over a successful login.
      if (!redirecting) setPending(null);
    }
  }

  async function handleResend() {
    resetMessages();
    setPending("resend");
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({ phone });
      if (otpError) {
        setError(friendlyError(otpError.message, "code"));
        startCooldown(phone, parseRetryAfter(otpError.message) ?? RESEND_COOLDOWN_SECONDS);
        return;
      }
      setCode(""); // drop the now-invalid code from the previous SMS
      setNotice("A new code has been sent.");
      startCooldown(phone);
      codeInputRef.current?.focus();
    } catch (err) {
      setError(getErrorMessage(err, "code"));
    } finally {
      setPending(null);
    }
  }

  function handleChangeNumber() {
    setStep("phone");
    setCode("");
    // The cooldown deliberately survives: it's keyed to the number it applies to,
    // so a different number sends immediately while the same one still waits.
    resetMessages();
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <div className="rounded-3xl border border-line bg-surface p-8 shadow-sm">
          {step === "phone" ? (
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-ink">Sign in</h1>
              <p className="mt-2 text-sm leading-relaxed text-body">
                Enter your phone number and we&apos;ll text you a 6-digit code.
              </p>

              <form onSubmit={handleSendCode} className="mt-6 space-y-4" noValidate>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-ink">
                    Phone number
                  </label>
                  <div className="mt-1.5 flex items-center rounded-full border border-line bg-surface shadow-sm transition-colors focus-within:border-brand has-[input:disabled]:opacity-60">
                    <span className="select-none py-3.5 pl-5 pr-3 text-base font-medium text-ink">
                      {NIGERIA_PREFIX}
                    </span>
                    <span className="h-6 w-px shrink-0 bg-line" aria-hidden />
                    <input
                      ref={phoneInputRef}
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      placeholder="801 234 5678"
                      value={phoneInput}
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        setError(null);
                        setFieldInvalid(false);
                      }}
                      disabled={busy}
                      aria-invalid={fieldInvalid || undefined}
                      aria-describedby={error ? "login-error" : undefined}
                      className="w-full rounded-r-full bg-transparent py-3.5 pl-3 pr-5 text-base text-ink placeholder:text-muted focus:outline-none"
                    />
                  </div>
                </div>

                {error && (
                  <p id="login-error" role="alert" className="text-sm text-red-600">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={busy} className={primaryButton}>
                  {pending === "send" ? "Sending…" : "Send code"}
                </button>
              </form>
            </div>
          ) : (
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-ink">
                Enter your code
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-body">
                Enter the 6-digit code sent to{" "}
                <span className="font-semibold text-ink">{phone}</span>.
              </p>

              <form onSubmit={handleVerify} className="mt-6 space-y-4" noValidate>
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-ink">
                    6-digit code
                  </label>
                  <input
                    ref={codeInputRef}
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setError(null);
                      setFieldInvalid(false);
                    }}
                    disabled={busy}
                    aria-invalid={fieldInvalid || undefined}
                    aria-describedby={error ? "login-error" : undefined}
                    className="mt-1.5 w-full rounded-full border border-line bg-surface py-3.5 text-center text-lg font-semibold tracking-[0.4em] text-ink shadow-sm placeholder:font-normal placeholder:tracking-[0.4em] placeholder:text-muted focus:border-brand focus:outline-none disabled:opacity-60"
                  />
                </div>

                {error && (
                  <p id="login-error" role="alert" className="text-sm text-red-600">
                    {error}
                  </p>
                )}

                {notice && (
                  <p role="status" className="text-sm font-medium text-brand-dark">
                    {notice}
                  </p>
                )}

                <button type="submit" disabled={busy} className={primaryButton}>
                  {pending === "verify" ? "Verifying…" : "Verify"}
                </button>
              </form>

              <div className="mt-5 flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleChangeNumber}
                  disabled={busy}
                  className="font-medium text-body transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={busy || cooldown > 0}
                  className="font-semibold text-brand transition-colors hover:text-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {pending === "resend"
                    ? "Resending…"
                    : cooldown > 0
                      ? `Resend in ${cooldown}s`
                      : "Resend code"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
